import { useMemo, useRef, useState } from "react";
import PlateInput, { isValidPlate } from "../components/PlateInput";
import PdfExportButton from "../components/PdfExportButton";
import FipVersionFilter from "../components/FipeVersionFilter";
import "./PlacaFipe.css";

/** Ordena itens FIPE pelo score (desc) */
const orderByScore = (list = []) =>
  [...list]
    .map((x) => ({ ...x, _score: Number(x.score) || 0 }))
    .sort((a, b) => b._score - a._score);

/** Base da API vinda do .env
 *  DEV  : REACT_APP_API_URL=http://localhost:4000/api
 *  PROD : REACT_APP_API_URL=/api
 */
const API_URL = (process.env.REACT_APP_API_URL || "/api").replace(/\/+$/, "");

export default function PlacaFipe() {
  const [placa, setPlaca] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [dados, setDados] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showAll, setShowAll] = useState(false); // auditoria

  // elemento que será exportado no PDF
  const printableRef = useRef(null);

  // Abort controller da request
  const abortRef = useRef(null);

  const fipe = useMemo(() => orderByScore(dados?.fipe?.dados || []), [dados]);

  async function consultar(p) {
    const up = (p || placa).toUpperCase().trim();
    setErro("");
    setDados(null);
    setSelected(null);
    setShowAll(false); // ao fazer nova consulta, volta a filtrar

    if (!isValidPlate(up)) {
      setErro("Placa inválida. Use ABC1D23 (Mercosul) ou ABC1234 (antigo).");
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      setLoading(true);
      const url = `${API_URL}/placa/${encodeURIComponent(up)}`;
      const r = await fetch(url, { signal: ctrl.signal, cache: "no-store" });
      const j = await r.json();
      if (!r.ok) {
        setErro(j?.message || "Erro na consulta.");
        return;
      }
      setDados(j);
    } catch (e) {
      if (e.name !== "AbortError") setErro("Falha ao conectar ao backend.");
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }

  // Renderizador único para os cards (reaproveitado nos dois modos)
  const renderCards = (list, meta) => (
    <>
      {/* DISCLAIMER */}
      {list.length > 0 && (
        <div
          className="pf-alert pf-alert-info"
          style={{
            marginBottom: ".5rem",
            padding: ".5rem .75rem",
            borderRadius: "8px",
            background: "rgba(0, 128, 255, 0.07)",
            border: "1px solid rgba(0, 128, 255, 0.25)",
            fontSize: ".95rem",
          }}
        >
          <b>Aviso:</b> confira se o <b>modelo do seu veículo</b> (
          <i>{dados?.MODELO || dados?.modelo || "—"}</i>) corresponde ao que está
          sendo exibido nos resultados FIPE. A <b>primeira opção</b> aparece
          <span style={{ color: "#0a7a27" }}> destacada em verde</span> por ser a de
          <b> maior probabilidade</b>.
        </div>
      )}

      {list.length === 0 ? (
        <div className="pf-muted">Esta consulta não retornou itens FIPE.</div>
      ) : (
        <>
          {meta && !meta.fellBackToAll && !showAll && (
            <div className="pf-hint">
              Filtrado por: <b>{meta.usedKey}</b> — mostrando {list.length}
              {typeof meta.total === "number" ? ` de ${meta.total}` : null}
            </div>
          )}
          {meta && meta.fellBackToAll && !showAll && (
            <div className="pf-muted">Nenhuma correspondência — exibindo todas.</div>
          )}

          <div className="pf-cards">
            {list.map((it, idx) => {
              const isTop = idx === 0; // primeira resposta
              return (
                <button
                  key={`${it.codigo_fipe}-${it.ano_modelo}-${idx}`}
                  type="button"
                  onClick={() => setSelected(idx)}
                  className={`pf-card ${selected === idx ? "is-selected" : ""}`}
                  style={
                    isTop
                      ? {
                          // destaque verde para a 1ª resposta (maior score)
                          border: "1.5px solid #19a34a",
                          boxShadow: "0 0 0 3px rgba(25,163,74,0.15)",
                          background:
                            "linear-gradient(0deg, rgba(25,163,74,0.06), rgba(25,163,74,0.06))",
                        }
                      : undefined
                  }
                >
                  <div className="pf-card-head" style={{ alignItems: "center" }}>
                    <div className="pf-card-title">{it.texto_modelo || "-"}</div>

                    <div className="pf-badge pf-badge-ghost">
                      score: {Number(it.score) || 0}
                    </div>

                    {isTop && (
                      <span
                        className="pf-badge"
                        style={{
                          marginLeft: ".5rem",
                          background: "#19a34a",
                          color: "white",
                          borderColor: "#19a34a",
                        }}
                        title="Primeira resposta destacada em verde por ter o maior score"
                      >
                        Top por score (API)
                      </span>
                    )}
                  </div>

                  <div className="pf-card-line">
                    <span className="pf-k">FIPE</span>
                    <span className="pf-v">{it.codigo_fipe || "-"}</span>
                  </div>
                  <div className="pf-card-line">
                    <span className="pf-k">Ano</span>
                    <span className="pf-v">{it.ano_modelo || "-"}</span>
                  </div>
                  <div className="pf-card-line">
                    <span className="pf-k">Combustível</span>
                    <span className="pf-v">
                      {it.combustivel || "-"}{" "}
                      {it.sigla_combustivel ? `(${it.sigla_combustivel})` : ""}
                    </span>
                  </div>
                  <div className="pf-card-line">
                    <span className="pf-k">Mês ref.</span>
                    <span className="pf-v">
                      {String(it.mes_referencia || "").trim() || "-"}
                    </span>
                  </div>

                  <div className="pf-price">{it.texto_valor || "-"}</div>
                </button>
              );
            })}
          </div>

          {selected != null && list[selected] && (
            <div className="pf-selected">
              <b>Selecionado:</b> {list[selected].texto_modelo} —{" "}
              {list[selected].texto_valor} (FIPE {list[selected].codigo_fipe})
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <div className="pf-page">
      <h1 className="pf-title">Pesquisa por Placa → Dados &amp; FIPE</h1>

      {/* barra de busca e ações (não imprime) */}
      <div className="pf-search no-print">
        <PlateInput
          value={placa}
          onChange={setPlaca}
          onSubmit={consultar}
          disabled={loading}
          autoFocus
        />

        <div style={{ display: "flex", gap: ".5rem", justifyContent: "center" }}>
          {/* botão de PDF aparece quando já há dados */}
          {dados && (
            <PdfExportButton
              targetRef={printableRef}
              filename={`consulta-${dados?.placa || placa}.pdf`}
            />
          )}
        </div>

        {loading && <div className="pf-hint">Consultando…</div>}
        {erro && <div className="pf-alert pf-alert-error">{erro}</div>}
      </div>

      {/* conteúdo imprimível/exportável */}
      <div ref={printableRef}>
        {dados && (
          <div className="pf-content">
            {/* Resumo */}
            <section className="pf-section">
              <div className="pf-section-head">
                <h2 className="pf-section-title">Resumo do veículo</h2>
              </div>

              <div className="pf-grid">
                <div className="pf-kv">
                  <span className="pf-k">Placa</span>
                  <span className="pf-v">{dados?.placa || dados?.PLACA || "-"}</span>
                </div>
                <div className="pf-kv">
                  <span className="pf-k">Placa (Mercosul)</span>
                  <span className="pf-v">{dados?.extra?.placa_modelo_novo || "-"}</span>
                </div>

                <div className="pf-kv">
                  <span className="pf-k">Marca</span>
                  <span className="pf-v">{dados?.MARCA || dados?.marca || "-"}</span>
                </div>
                <div className="pf-kv">
                  <span className="pf-k">Modelo</span>
                  <span className="pf-v">{dados?.MODELO || dados?.modelo || "-"}</span>
                </div>

                <div className="pf-kv">
                  <span className="pf-k">Versão/Submodelo</span>
                  <span className="pf-v">{dados?.VERSAO || dados?.SUBMODELO || "-"}</span>
                </div>
                <div className="pf-kv">
                  <span className="pf-k">Ano Fab./Modelo</span>
                  <span className="pf-v">
                    {dados?.extra?.ano_fabricacao || "-"} /{" "}
                    {dados?.anoModelo || dados?.extra?.ano_modelo || "-"}
                  </span>
                </div>

                <div className="pf-kv">
                  <span className="pf-k">Combustível</span>
                  <span className="pf-v">{dados?.extra?.combustivel || "-"}</span>
                </div>
                <div className="pf-kv">
                  <span className="pf-k">UF/Mun.</span>
                  <span className="pf-v">
                    {dados?.uf || "-"} / {dados?.municipio || "-"}
                  </span>
                </div>

                <div className="pf-kv">
                  <span className="pf-k">Origem</span>
                  <span className="pf-v">
                    {dados?.origem || dados?.nacionalidade || "-"}
                  </span>
                </div>
                <div className="pf-kv">
                  <span className="pf-k">Situação</span>
                  <span className="pf-v">{dados?.situacao || "-"}</span>
                </div>
              </div>

              <div className="pf-meta">
                {dados?.logo && (
                  <img className="pf-logo" src={dados.logo} alt="logo da marca" />
                )}
                <span className="pf-muted">
                  Data consulta:{" "}
                  {new Date(dados.consultadoEm || Date.now()).toLocaleString()}
                </span>
              </div>
            </section>

            {/* FIPE */}
            <section className="pf-section">
              <div
                className="pf-section-head"
                style={{ gap: ".5rem", display: "flex", alignItems: "center" }}
              >
                <h2 className="pf-section-title">FIPE</h2>
                {fipe.length > 0 && (
                  <>
                    <span className="pf-badge">
                      Mês ref.: {String(fipe[0].mes_referencia || "").trim()}
                    </span>
                    <button
                      type="button"
                      className="pf-btn pf-btn-ghost"
                      onClick={() => {
                        setShowAll((v) => !v);
                        setSelected(null);
                      }}
                      style={{ marginLeft: "auto" }}
                    >
                      {showAll
                        ? "Exibir Somente Correspondentes"
                        : "Exibir Todos os Resultados"}
                    </button>
                  </>
                )}
              </div>

              {fipe.length === 0 ? (
                <div className="pf-muted">Esta consulta não retornou itens FIPE.</div>
              ) : showAll ? (
                // modo auditoria: exibe todos
                renderCards(fipe, {
                  fellBackToAll: true,
                  usedKey: null,
                  total: fipe.length,
                })
              ) : (
                // modo filtrado pelo modelo
                <FipVersionFilter
                  modelText={dados?.MODELO || dados?.modelo || ""}
                  items={fipe}
                >
                  {(list, meta) => renderCards(list, meta)}
                </FipVersionFilter>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
